/**
 * Plugin client context providing collaborators to plugin hooks.
 *
 * The web app mounts a PluginClientProvider that supplies concrete
 * implementations of auth, router, socket, and API collaborators.
 * Plugin hooks consume these via the context rather than importing
 * web-specific singletons.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type {
  PluginCollaborators,
  PluginAuthState,
  PluginRouterAdapter,
  PluginSocketLike,
  PluginApiClient,
  PluginRegistryValue,
} from './interfaces';
import { ROUTES, withOrgSlug } from './routes';

const PluginClientContext = createContext<PluginCollaborators | null>(null);
export const AuthContext = createContext<PluginAuthState | null>(null);

/**
 * Context for pre-resolved plugin registries.
 *
 * The web app populates this with fully-resolved plugin surfaces
 * (flowTriggerRenderers, eventLabels, etc.) built from manifest data
 * and the component/icon maps from plugin registrations.
 *
 * Plugin storybooks can inject a subset of resolved data for testing
 * cross-plugin integration without depending on the full web bundle.
 */
export const PluginRegistryContext = createContext<PluginRegistryValue | null>(null);

export interface PluginRegistryProviderProps {
  value: PluginRegistryValue;
  children: ReactNode;
}

export function PluginRegistryProvider({ value, children }: PluginRegistryProviderProps) {
  return <PluginRegistryContext.Provider value={value}>{children}</PluginRegistryContext.Provider>;
}

export function usePluginRegistry(): PluginRegistryValue | null {
  return useContext(PluginRegistryContext);
}

const defaultSocket: PluginSocketLike = {
  subscribe: () => {},
  unsubscribe: () => {},
  onMessage: () => () => {},
  connected: false,
  onConnectionChange: () => () => {},
  addReconnectCallback: () => {},
  removeReconnectCallback: () => {},
};

function useCollaborators(): PluginCollaborators {
  const ctx = useContext(PluginClientContext);
  if (!ctx) {
    throw new Error(
      'Plugin hooks require PluginClientProvider. ' +
        'Ensure the web app mounts PluginClientProvider in App.tsx.',
    );
  }
  return ctx;
}

export interface PluginClientProviderProps {
  auth: PluginAuthState;
  router: PluginRouterAdapter;
  socket: PluginSocketLike;
  api: PluginApiClient;
  children: ReactNode;
}

export function PluginClientProvider({
  auth,
  router,
  socket,
  api,
  children,
}: PluginClientProviderProps) {
  const value = useMemo(() => ({ auth, router, socket, api }), [auth, router, socket, api]);
  return <PluginClientContext.Provider value={value}>{children}</PluginClientContext.Provider>;
}

export function usePluginAuth(): PluginAuthState {
  const authContext = useContext(AuthContext);
  return authContext ?? useCollaborators().auth;
}

export const useAuth = usePluginAuth;

export function usePluginRouter(): PluginRouterAdapter {
  return useCollaborators().router;
}

export function usePluginSocket(): PluginSocketLike {
  return useContext(PluginClientContext)?.socket ?? defaultSocket;
}

export function usePluginApi(): PluginApiClient {
  return useCollaborators().api;
}

export function useOrgKey(): string {
  const ctx = useContext(PluginClientContext);
  const auth = useContext(AuthContext) ?? ctx?.auth;
  return auth?.activeOrganizationId ?? 'global';
}

function resolveActiveOrgSlug(
  ctx: PluginCollaborators | null,
  auth: PluginAuthState | null | undefined,
): string {
  const routeOrgSlug = ctx?.router.params.orgSlug;
  const memberships = auth?.memberships ?? [];
  const routeMatchesMembership = memberships.some((m) => m.organization.slug === routeOrgSlug);
  if (routeOrgSlug && (routeMatchesMembership || memberships.length === 0)) {
    return routeOrgSlug;
  }
  return auth?.activeMembership?.organization.slug || '_';
}

export function useActiveOrgSlug(): string {
  const ctx = useContext(PluginClientContext);
  const auth = useContext(AuthContext) ?? ctx?.auth;
  return resolveActiveOrgSlug(ctx, auth);
}

export type OrgRoutesResult = ReturnType<typeof ROUTES.org> & {
  orgSlug: string;
  withOrg: (path: string) => string;
};

export function useOrgRoutes(): OrgRoutesResult {
  const ctx = useContext(PluginClientContext);
  const auth = useContext(AuthContext) ?? ctx?.auth;
  const orgSlug = resolveActiveOrgSlug(ctx, auth);

  return useMemo(() => {
    const routes = ROUTES.org(orgSlug);
    return {
      ...routes,
      orgSlug,
      withOrg: (path: string) => withOrgSlug(orgSlug, path),
    };
  }, [orgSlug]);
}

export { PluginClientContext };
export type { PluginAuthState };
